import { ActionMenuItem, ControlledNumberField, ControlledSelect, ControlledDateOnlyPicker, FieldGrid } from '@if/mui'
import { EPaymentPlan } from '@if/core/types'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { getFirstPaymentOnMinMax, ProductsWorksheet } from '@src/types/WorksheetSchema'
import { JSX, useMemo } from 'react'
import { Control, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  creditApplication: CreditApplication
  control: Control<ProductsWorksheet>
  disabledAmountInput?: boolean
  maxTermDuration?: number
  loanTermChoicesList: JSX.Element[]
}

const ProductsWorksheetDetails = ({ creditApplication, control, disabledAmountInput, loanTermChoicesList }: Props) => {
  const { t } = useTranslation()
  const [paymentFrequency, deliveryOn] = useWatch({ control, name: ['paymentFrequency', 'deliveryOn'] })
  const maxActivationDate = useMemo(() => new Date(creditApplication.expiresOn), [creditApplication.expiresOn])

  const [minFirstPaymentDate, maxFirstPaymentDate] = useMemo(() => {
    return getFirstPaymentOnMinMax(deliveryOn, paymentFrequency)
  }, [deliveryOn, paymentFrequency])

  const programConfig = programConfigByFinancingProgramId(creditApplication.financingProgramId)
  const availableFrequencyPayments = programConfig.frequencyPayments

  const paymentFrequencyOptions = useMemo(
    () =>
      availableFrequencyPayments.map((value) => (
        <ActionMenuItem key={value} value={value}>
          {t(`EPaymentFrequency.${value}`, { ns: 'enum' })}
        </ActionMenuItem>
      )),
    [t, availableFrequencyPayments],
  )

  return (
    <FieldGrid columns={{ xs: 2, md: 3 }}>
      <ControlledNumberField
        control={control}
        name={`amountRequested`}
        label={t('worksheet.requestedAmount')}
        disabled={disabledAmountInput}
        fullWidth
        isMoney
      />
      <ControlledDateOnlyPicker
        name="deliveryOn"
        control={control}
        label={t('worksheet.activationDate')}
        disablePast
        minDate={new Date()}
        maxDate={maxActivationDate}
        fullWidth
      />
      <ControlledSelect
        name="term"
        control={control}
        options={loanTermChoicesList}
        label={t('worksheet.loanTerm')}
        disableEmptyOption
        fullWidth
      />
      <ControlledSelect
        name="paymentFrequency"
        control={control}
        options={paymentFrequencyOptions}
        label={t('worksheet.paymentFrequency')}
        disableEmptyOption
        fullWidth
      />
      <ControlledDateOnlyPicker
        name="firstPaymentOn"
        control={control}
        label={t('worksheet.firstPaymentOn')}
        disablePast
        minDate={minFirstPaymentDate}
        maxDate={maxFirstPaymentDate}
        disabled={!paymentFrequency}
        fullWidth
      />
      <ControlledSelect
        name="paymentPlanId"
        control={control}
        label={t('worksheet.paymentPlan')}
        disableEmptyOption
        disabled
      >
        <ActionMenuItem value={EPaymentPlan.RegularDailyInterests}>
          {t(`EPaymentPlan.${EPaymentPlan.RegularDailyInterests}`, { ns: 'enum' })}
        </ActionMenuItem>
      </ControlledSelect>
    </FieldGrid>
  )
}

export default ProductsWorksheetDetails
